// @ts-nocheck
// TODO: tighten types after migration
// src/Store/StoreProductReview.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import { getAuthUser, getAuthToken } from "../utils/auth";
import "./StoreProductReview.css";

export default function StoreProductReview({ productId }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [commentText, setCommentText] = useState("");
    const [purchaseId, setPurchaseId] = useState("");
    const [starRating, setStarRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [reviews, setReviews] = useState([]);
    const [replyTo, setReplyTo] = useState(null);
    const [isPosting, setIsPosting] = useState(false);
    const [purchaseIdError, setPurchaseIdError] = useState("");

    const user = getAuthUser();
    const token = getAuthToken();

    const inputRef = useRef(null);  // ✅ Ref for the textarea

    // 🔃 Fetch reviews every 5 seconds
    useEffect(() => {
      let intervalId;

      const fetchReviews = async () => {
        try {
          const res = await axios.get(API_ENDPOINTS.STORE.PRODUCT_REVIEWS(productId));
          setReviews(res.data);
        } catch (err) {
          console.error("Error fetching product reviews:", err);
        }
      };

      fetchReviews();
      intervalId = setInterval(fetchReviews, 5000);

      return () => clearInterval(intervalId);
    }, [productId]);

    // ✅ Scroll to textarea when replyTo changes
    useEffect(() => {
      if (replyTo && inputRef.current) {
        inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        inputRef.current.focus();
      }
    }, [replyTo]);

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!user || !token) {
        navigate("/signin", { state: { from: location } });
        return;
      }

      // For top-level reviews (not replies), require purchaseId and starRating
      if (!replyTo) {
        if (!purchaseId.trim()) {
          setPurchaseIdError("Purchase ID is required");
          return;
        }
        if (starRating === 0) {
          setPurchaseIdError("Please select a star rating");
          return;
        }
      }

      if (!commentText.trim()) {
        return; // Don't submit if comment is empty
      }

      setIsPosting(true);
      setPurchaseIdError("");

      try {
        const res = await axios.post(
          API_ENDPOINTS.STORE.PRODUCT_REVIEWS_POST,
          {
            productId,
            userId: user._id,
            purchaseId: replyTo ? null : purchaseId, // Only send purchaseId for top-level reviews
            starRating: replyTo ? null : starRating, // Only send starRating for top-level reviews
            commentText,
            nestedId: replyTo || null,
          },
          {
            withCredentials: true,
            headers: {
              "Authorization": `Bearer ${token}`,
            }
          }
        );

        setCommentText("");
        setPurchaseId("");
        setStarRating(0);
        setHoverRating(0);
        setReplyTo(null);
        setReviews([res.data, ...reviews]);
      } catch (err) {
        console.error("Failed to post product review:", err);
        if (err.response && err.response.data && err.response.data.message) {
          setPurchaseIdError(err.response.data.message);
        } else {
          alert("Couldn't post review");
        }
      } finally {
        setIsPosting(false);
      }
    };

    
    const formatTimestamp = (timestamp) => {
      const now = new Date();
      const commentDate = new Date(timestamp);
      const diffMs = now - commentDate;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

      if (diffHours < 24) {
        return diffHours === 0 ? "Just now" : `${diffHours} hr${diffHours > 1 ? "s" : ""} ago`;
      } else {
        const day = String(commentDate.getDate()).padStart(2, '0');
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[commentDate.getMonth()];
        const year = String(commentDate.getFullYear());

        let hours = commentDate.getHours();
        const minutes = String(commentDate.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;

        return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
      }
    };

    const renderStarRating = (rating) => {
      return (
        <div className="star-rating-display ml-[8px] inline-flex flex-row gap-[2px]">
          {[1, 2, 3, 4, 5].map((star) => (
            <span key={star} className={star <= rating ? "star filled text-[#ffd700]" : "star text-[#ddd]"}>
              ★
            </span>
          ))}
        </div>
      );
    };

    const renderReviews = (parentId = null, level = 0) => {
      return reviews
        .filter((r) => r.nestedId === parentId)
        .map((r) => (
          <div key={r._id} className={`comment-container mt-[20px] ${level === 1 ? "comment-reply ml-[2%] border-l border-slider p-[1rem_0_1rem_1.5rem]" : ""}`}>
            <div className="comment-header flex flex-row items-center justify-between text-[1.1em] font-[700] text-text">
              <div className="comment-header-left flex flex-row items-center gap-[8px]">
                {r.userName}
                {r.starRating && level === 0 && (
                  <span className="star-rating-inline inline-flex items-center">
                    {renderStarRating(r.starRating)}
                  </span>
                )}
              </div>
              <span className="comment-date ml-[8px] text-[0.7em] font-[400] text-post-content">{formatTimestamp(r.timestamp)}</span>
            </div>
            <div className="comment-text mt-[4px] text-[1em] text-post-content">{r.commentText}</div>
            {user && level === 0 && (
              <button onClick={() => setReplyTo(r._id)} className="reply-button mt-[6px] mb-[10px] border-none bg-none p-0 text-[0.9em] text-[#47b5ff] hover:underline">Reply</button>
            )}
            {level === 0 && renderReviews(r._id, 1)}
          </div>
        ));
    };

    return (
      <div className="store-product-review-wrapper relative z-[10] mt-[5%] rounded-[20px] border border-slider">
        {(!user || !token) && (
          <div className="overlay-login absolute left-0 top-0 z-[10] flex h-full w-full items-center justify-center rounded-[20px] backdrop-blur-[3px]">
            <button
              onClick={() => navigate("/signin", { state: { from: location } })}
              className="login-overlay-btn z-[11] rounded-[6px] border-none bg-[#47b5ff] px-[20px] py-[10px] text-[14px] text-white"
            >
              Login to Review
            </button>
          </div>
        )}

        <div className={`store-product-review m-[2%] rounded-[20px] p-[1rem] ${!user || !token ? "blurred relative pointer-events-none select-none blur-[3px]" : ""}`}>
          <h3 className="mb-[12px] text-[1.5em] text-text">Share your thoughts</h3>
          <form onSubmit={handleSubmit}>
            {/* Purchase ID and Star Rating - Only for top-level reviews */}
            {!replyTo && (
              <div className="review-fields mb-[12px] flex flex-row flex-wrap gap-[1rem] max-[767px]:flex-col">
                <div className="field-group flex min-w-[200px] flex-1 flex-col gap-[6px] max-[767px]:min-w-full">
                  <label htmlFor="purchaseId" className="text-[0.9em] font-[500] text-text">Purchase ID *</label>
                  <input
                    type="text"
                    id="purchaseId"
                    value={purchaseId}
                    onChange={(e) => {
                      setPurchaseId(e.target.value);
                      setPurchaseIdError("");
                    }}
                    placeholder="Enter your purchase order ID"
                    required
                    disabled={!user || !token}
                    className={`h-[44px] w-full rounded-[6px] border border-solid bg-bg px-[12px] py-[10px] text-[1em] text-text transition-colors duration-300 focus:outline-none ${purchaseIdError ? 'error border-[#ff4444]' : 'border-slider focus:border-text'}`}
                  />
                  {purchaseIdError && <span className="error-message mt-[4px] text-[0.85em] text-[#ff4444]">{purchaseIdError}</span>}
                </div>
                <div className="field-group flex min-w-[200px] flex-1 flex-col gap-[6px] max-[767px]:min-w-full">
                  <label htmlFor="starRating" className="text-[0.9em] font-[500] text-text">Rating *</label>
                  <div className={`star-rating-input flex h-[44px] items-center rounded-[6px] border border-solid bg-bg px-[12px] gap-[8px] transition-colors duration-300 ${purchaseIdError && starRating === 0 ? 'border-[#ff4444]' : 'border-slider focus-within:border-text'}`}>
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isFilled = star <= (hoverRating || starRating);
                      return (
                        <button
                          key={star}
                          type="button"
                          className={`star-button border-none bg-transparent p-0 text-[1.4em] leading-none transition-all duration-150 ${isFilled ? 'filled text-[#ffd700]' : 'text-[#ddd]'} hover:text-[#ffd700] hover:scale-115 disabled:cursor-not-allowed disabled:opacity-60`}
                          onClick={() => {
                            setStarRating(star);
                            setPurchaseIdError("");
                          }}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          disabled={!user || !token}
                          title={`${star} star${star > 1 ? 's' : ''}`}
                        >
                          ★
                        </button>
                      );
                    })}
                    {starRating > 0 && (
                      <span className="ml-auto text-[0.85em] font-[500] text-post-content">
                        {starRating} / 5
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <textarea
              ref={inputRef}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={replyTo ? "Replying..." : "Write a review"}
              rows="3"
              required
              disabled={!user || !token}
              className="w-full resize-y rounded-[6px] border border-solid border-slider bg-bg px-[12px] py-[10px] text-[1em] text-text transition-colors duration-300 focus:border-text focus:outline-none"
            />
            {replyTo && (
              <p className="replying-note mt-[6px] text-[1em] text-text">
                Replying to review —
                <button type="button" className="ml-[4px] border-none bg-none p-0 text-[1em] text-[#47b5ff] hover:underline" onClick={() => setReplyTo(null)}>Cancel</button>
              </p>
            )}
            <button 
              type="submit" 
              disabled={!user || !token || !commentText.trim() || isPosting}
              className={`mt-[10px] flex min-w-[60px] items-center justify-center rounded-[6px] border-none px-[14px] py-[6px] text-[1em] transition-colors duration-300 ${(!user || !token || !commentText.trim() || isPosting) ? 'cursor-not-allowed bg-[#cccccc] text-[#666666] opacity-60' : 'bg-[#47b5ff] text-bg hover:bg-text'} ${isPosting ? 'posting' : ''}`}
            >
              {isPosting ? (
                <div className="comment-loader"></div>
              ) : (
                "Post"
              )}
            </button>
          </form>

          <div className="comments-list mt-[2rem]">
            <h4 className="mb-[12px] text-[1.5em] text-text">Reviews</h4>
            {renderReviews()}
          </div>
        </div>
      </div>
    );
}


